import { SIGNUP_MODAL } from "common/modals/SignupModal";
import { LOGIN_MODAL_OVERLAY } from "common/styleguide/LoginModalOverlay";
import { showModal } from "lib/modalUtils";
import { trackEvent } from "lib/trackEvent";
import { uuid } from "lib/utils";
import { initialMapSettings } from "../Settings";

interface plannerState {
  leftCollapse: boolean;
  rightCollapse: boolean;
  sgCollapse: boolean;
  showCueReview: boolean;
  cueReviewKey: string | undefined;
  showRouteSplitter: boolean;
  showTraceTool: boolean;
  showSave: boolean;
  showSignup: boolean;
  distanceMarkersWas: boolean | undefined;
  mapSettings: any;
  clickActionCommand: string;
  hoveredFeature: boolean | null;
  selectedFeature: boolean | null;
}

// NOTE about clickActionCommand: historically, this state was initially set
// via `selectedCommand`, which was determined from the
// route_planner_follow_roads user preference.  however, as of october 2020,
// this preference is not sticky in the new route planner, and according to
// https://github.com/ridewithgps/ridewithgps/blob/8051853bfd699a656d99e5e15c57b60ce4544519/public/javascripts/routes/new.js#L1474
// should be removed. (further, this pref isn't set anywhere in either codebase)
export const initialState: plannerState = {
  leftCollapse: false,
  rightCollapse: false,
  sgCollapse: false,
  showCueReview: false,
  cueReviewKey: undefined,
  showRouteSplitter: false,
  showTraceTool: false,
  showSave: false,
  showSignup: false,
  distanceMarkersWas: undefined,
  mapSettings: initialMapSettings,
  clickActionCommand: "followRoads",
  hoveredFeature: null,
  selectedFeature: null,
};

type reducerAction = booleanAction | setClickAction | anyAction;
export const reducer = (state: plannerState, action: reducerAction): plannerState => {
  const { value } = action;
  switch (action.type) {
    case "setShowCueReview":
      return action.value
        ? {
            ...state,
            showCueReview: true,
            cueReviewKey: uuid(),
            distanceMarkersWas: state.mapSettings.showDistanceMarkers,
            mapSettings: { ...state.mapSettings, showDistanceMarkers: false },
          }
        : {
            ...state,
            showCueReview: false,
            mapSettings: {
              ...state.mapSettings,
              showDistanceMarkers: state.distanceMarkersWas,
            },
            distanceMarkersWas: undefined,
          };
    case "forceRerenderCueReview":
      return { ...state, cueReviewKey: uuid() };
    case "setShowRouteSplitter":
      return { ...state, showRouteSplitter: action.value };
    case "setShowTraceTool":
      return { ...state, showTraceTool: action.value };
    case "setShowSave":
      if (state.showSave === value) return state;
      return {
        ...state,
        showSave: value,
        leftCollapse: value,
        rightCollapse: value,
        sgCollapse: value,
      };
    case "setShowSignup":
      if (state.showSignup === value) return state;
      showModal(SIGNUP_MODAL, {
        onRequestLogin: () => showModal(LOGIN_MODAL_OVERLAY),
      });
      return { ...state, showSignup: value };
    case "toggleLeft":
      trackEvent({
        category: "Route Planner",
        action: "Collapse left panel",
        label: undefined,
      });
      return { ...state, leftCollapse: !state.leftCollapse };
    case "toggleRight":
      trackEvent({
        category: "Route Planner",
        action: "Collapse right panel",
        label: undefined,
      });
      return { ...state, rightCollapse: !state.rightCollapse };
    case "setSgCollapse":
      return { ...state, sgCollapse: value };
    case "setMapSettings":
      return { ...state, mapSettings: value };
    case "setClickActionCommand":
      return { ...state, clickActionCommand: value };
    case "setHoveredFeature":
      return { ...state, hoveredFeature: value };
    case "setSelectedFeature":
      return { ...state, selectedFeature: value };
    default:
      return state;
  }
};

type booleanActions =
  | "setShowCueReview"
  | "setShowRouteSplitter"
  | "setShowTraceTool"
  | "setShowSave"
  | "setShowSignup"
  | "setSgCollapse";

interface booleanAction {
  type: booleanActions;
  value: boolean;
}

type clickActions =
  | "optimizeForDefault"
  | "optimizeForCallback"
  | "avoidHighwaysChanged"
  | "setCallbacks"
  | "unsetCallbacks";

interface setClickAction {
  type: "setClickActionCommand";
  value: clickActions;
}

interface anyAction {
  type: string;
  value: any;
}

export const actions = {
  setShowCueReview: (value: boolean): booleanAction => ({
    type: "setShowCueReview",
    value,
  }),
  forceRerenderCueReview: { type: "forceRerenderCueReview" },
  setShowRouteSplitter: (value: boolean): booleanAction => ({
    type: "setShowRouteSplitter",
    value,
  }),
  setShowTraceTool: (value: boolean): booleanAction => ({
    type: "setShowTraceTool",
    value,
  }),
  setShowSave: (value: boolean): booleanAction => ({
    type: "setShowSave",
    value,
  }),
  setShowSignup: (value: boolean): booleanAction => ({
    type: "setShowSignup",
    value,
  }),
  toggleLeft: { type: "toggleLeft" },
  toggleRight: { type: "toggleRight" },
  setSgCollapse: (value: boolean): booleanAction => ({
    type: "setSgCollapse",
    value,
  }),
  setMapSettings: (value: any): anyAction => ({
    type: "setMapSettings",
    value,
  }),
  setClickActionCommand: (value: clickActions): setClickAction => ({
    type: "setClickActionCommand",
    value,
  }),
  setHoveredFeature: (value: any): anyAction => ({
    type: "setHoveredFeature",
    value,
  }),
  setSelectedFeature: (value: any): anyAction => ({
    type: "setSelectedFeature",
    value,
  }),
};
